// Tiny API client. Token comes from the /web deep link (?t=) and is kept in
// localStorage; every request sends it as a Bearer token. (06 §Auth)

const TOKEN_KEY = "tk_token";

export function captureToken(): void {
  const url = new URL(location.href);
  const t = url.searchParams.get("t");
  if (t) {
    localStorage.setItem(TOKEN_KEY, t);
    url.searchParams.delete("t");
    history.replaceState(null, "", url.toString());
  }
}

export function token(): string {
  return localStorage.getItem(TOKEN_KEY) ?? "";
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { ...(init?.headers ?? {}), Authorization: `Bearer ${token()}` },
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json() as Promise<T>;
}

export interface TripCard {
  id: string;
  name: string;
  baseCurrency: string;
  status: "active" | "closed";
  myNetMinor: number;
  memberCount: number;
  lastActivity: number;
}

export interface Member {
  userId: string;
  displayName: string;
  isGhost: boolean;
  role?: string;
}

export interface TripState {
  id: string;
  name: string;
  baseCurrency: string;
  status: "active" | "closed";
  createdAt: number;
  closedAt?: number;
  members: Member[];
  adminUserId: string;
  totals: {
    spentMinor: number;
    byCategory: Record<string, number>;
    byMember: Record<string, { paidMinor: number; owedMinor: number }>;
  };
}

export interface Expense {
  handle: number;
  id: string;
  description: string;
  category: string;
  paidBy: string;
  amountMinor: number;
  currency: string;
  baseAmountMinor: number;
  fxRate: number;
  fxSource: string;
  splitType: string;
  shares: { userId: string; owedBaseMinor: number }[];
  createdAt: number;
  updatedAt?: number;
  voidedAt?: number;
}

export interface Balance {
  userId: string;
  netBaseMinor: number;
}
export interface Settlement {
  from: string;
  to: string;
  amountBaseMinor: number;
}
export interface TripEvent {
  seq: number;
  type: string;
  actorUserId: string;
  summary: string;
  createdAt: number;
}

export const api = {
  trips: () => req<TripCard[]>("/api/trips"),
  trip: (id: string) => req<TripState>(`/api/trips/${id}`),
  ledger: (id: string, voided = false) =>
    req<Expense[]>(`/api/trips/${id}/ledger${voided ? "?include=voided" : ""}`),
  balances: (id: string) => req<Balance[]>(`/api/trips/${id}/balances`),
  settlement: (id: string) => req<Settlement[]>(`/api/trips/${id}/settlement`),
  history: (id: string) => req<TripEvent[]>(`/api/trips/${id}/history`),
  exportUrl: (id: string, format: "csv" | "json") =>
    `/api/trips/${id}/export?format=${format}&t=${encodeURIComponent(token())}`,
};
